import type { ServerMessage } from '../../shared/protocol';
import { reportDiagnostic } from '../platform/telemetry';

export type SubmissionOutcome = 'pending' | 'accepted' | 'duplicate' | 'rejected' | 'unconfirmed';

export interface Submission {
  id: string;
  round: string;
  word: string;
  /** The traced tiles, resent with the word; chaos pickups depend on it. */
  path: number[] | undefined;
  /** Captured during the original gesture, never inferred again on resend. */
  pickupIds: string[] | undefined;
  /**
   * Points the browser already presented for a solution-filter hit; the accepted cue has
   * played, so the server's confirmation only settles the entry. Undefined for words the
   * browser could not judge.
   */
  presumedPoints: number | undefined;
  /** Assistive-technology text for the verdict; the board styles `outcome` visually. */
  status: string;
  outcome: SubmissionOutcome;
  pending: boolean;
  repeated: boolean;
  /** False while the word waits for a live socket or a resync; the next snapshot flushes it. */
  sent: boolean;
  sends: number;
}
/** Words awaiting a server verdict; further attempts are refused until one settles. */
const MAX_PENDING = 10;
/** Distinct words attempted per round; well above the server's per-player acceptance cap. */
const MAX_ATTEMPTED_WORDS = 2000;
/** Rows kept for the HUD; never fewer than the pending cap so a settled row is always evictable. */
const MAX_ENTRIES = MAX_PENDING;
/** How long an unanswered send waits before a snapshot resync resolves or resends it. */
const STALL_MS = 1500;
/** Sends per word before the session stops chasing it; the player can enter the word again. */
const MAX_SENDS = 3;

/**
 * Bounded, same-round send queue. A word entered during a connection blip waits here and
 * is flushed after the recovery snapshot; the server's deadline and duplicate checks keep
 * this fair, so a late flush earns `late` and a repeat earns `dup`, never extra time or
 * points. Words never survive a round change.
 */
export class Submissions {
  entries: Submission[] = [];
  private sequence = 0;
  private attempted = new Set<string>();
  private round: string | null = null;
  private timers = new Map<string, ReturnType<typeof setTimeout>>();
  constructor(
    private readonly changed: () => void,
    private readonly stalled: () => void = () => {},
  ) {}

  add(
    round: string,
    word: string,
    path?: readonly number[],
    pickupIds?: readonly string[],
    presumedPoints?: number,
  ): Submission | undefined {
    if (this.entries.some((entry) => entry.pending && entry.word === word)) return;
    if (
      this.entries.filter((entry) => entry.pending).length >= MAX_PENDING ||
      this.attempted.size >= MAX_ATTEMPTED_WORDS
    )
      return;
    const entry: Submission = {
      id: String(++this.sequence),
      round,
      word,
      path: path ? [...path] : undefined,
      pickupIds: pickupIds?.length ? [...pickupIds] : undefined,
      presumedPoints,
      pending: true,
      status: 'Checking',
      outcome: 'pending',
      repeated: this.attempted.has(word),
      sent: false,
      sends: 0,
    };
    this.attempted.add(word);
    if (this.entries.length >= MAX_ENTRIES)
      this.entries.splice(
        this.entries.findIndex((item) => !item.pending),
        1,
      );
    this.entries = [...this.entries, entry];
    return entry;
  }

  /** The word is on the wire; an unanswered send asks for one resync, then a resend. */
  sent(entry: Submission): void {
    entry.sent = true;
    entry.sends++;
    clearTimeout(this.timers.get(entry.id));
    this.timers.set(
      entry.id,
      setTimeout(() => {
        this.timers.delete(entry.id);
        if (!entry.pending) return;
        entry.sent = false;
        reportDiagnostic({ kind: 'submission_waiting' });
        this.changed();
        this.stalled();
      }, STALL_MS),
    );
  }

  /** Anything in flight on the lost socket will be reconciled or resent after the snapshot. */
  disconnected(): void {
    for (const entry of this.entries) {
      if (!entry.pending) continue;
      entry.sent = false;
      clearTimeout(this.timers.get(entry.id));
      this.timers.delete(entry.id);
    }
  }

  /** Settle the matching entry; returns it when this verdict is what it was waiting for. */
  result(message: Extract<ServerMessage, { t: 'wordResult' }>): Submission | undefined {
    const entry = this.entries.find(
      (item) =>
        item.round === message.roundId &&
        item.word === message.w &&
        (message.submissionId ? item.id === message.submissionId : !item.repeated),
    );
    if (!entry || (!entry.pending && !message.ok)) return undefined;
    const wasPending = entry.pending;
    this.finish(
      entry,
      message.ok ? 'accepted' : message.reason === 'dup' ? 'duplicate' : 'rejected',
      message.ok
        ? 'Accepted'
        : message.reason === 'dup'
          ? 'Already found'
          : {
              short: 'Too short',
              long: 'Too long',
              notword: 'Not a word',
              notboard: 'Not on board',
              late: 'Time’s up',
              spectator: 'Spectating',
              round: 'Round changed',
              limit: 'Word limit reached',
              frozen: 'Frozen',
            }[message.reason],
    );
    return wasPending ? entry : undefined;
  }

  /**
   * Settle pending words against the authoritative snapshot and return the ones the
   * caller must (re)send now. Words already chased `MAX_SENDS` times settle unconfirmed.
   */
  reconcile(round: string | null, words: Map<string, number>): Submission[] {
    if (this.round !== round) {
      this.clear();
      this.round = round;
      return [];
    }
    const flush: Submission[] = [];
    for (const entry of this.entries) {
      if (!entry.pending) continue;
      if (words.has(entry.word)) {
        if (!entry.sent) reportDiagnostic({ kind: 'submission_recovered' });
        this.finish(entry, 'accepted', 'Accepted');
      } else if (entry.sent) continue;
      else if (entry.sends >= MAX_SENDS) this.finish(entry, 'unconfirmed', 'Unconfirmed');
      else flush.push(entry);
    }
    return flush;
  }

  private finish(entry: Submission, outcome: SubmissionOutcome, status: string): void {
    clearTimeout(this.timers.get(entry.id));
    this.timers.delete(entry.id);
    entry.pending = false;
    entry.sent = true;
    entry.outcome = outcome;
    entry.status = status;
  }

  clear(): void {
    for (const timer of this.timers.values()) clearTimeout(timer);
    this.timers.clear();
    this.entries = [];
    this.attempted.clear();
  }
}
